import { CalendarClock, FileCheck2, Sparkles, SearchCheck, House as Home, Maximize2, TrendingUp, Paintbrush, ArrowRight, ShieldCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
const timelineFactors = [{
  step: "01",
  title: "Design finalization",
  description: "Detailed 3D schematics, space reconfiguration plans, and electrical layouts locked before construction begins to avoid mid-build revisions.",
  icon: CalendarClock
}, {
  step: "02",
  title: "Material selection and ordering",
  description: "Securing custom cabinetry, natural stone slabs, architectural fixtures, and specialized flooring with verified lead times before demo.",
  icon: Sparkles
}, {
  step: "03",
  title: "City of Brookhaven permitting",
  description: "Submitting engineering plans and navigating municipal approvals for load-bearing wall removals, structural spans, and MEP updates.",
  icon: FileCheck2
}, {
  step: "04",
  title: "Required municipal inspections",
  description: "Scheduling framing, rough-in mechanical, electrical, plumbing, and final certificate of completion inspections per Brookhaven code.",
  icon: SearchCheck
}];
const interiorTriggers = [{
  title: "Want an open layout",
  description: "Removing compartmentalized walls between kitchen, living, and dining spaces to enhance natural light and entertainment flow.",
  icon: Maximize2
}, {
  title: "Bought an older Brookhaven home with a dated interior",
  description: "Modernizing vintage floor plans, aging subfloors, and compartmentalized rooms common in established local neighborhoods.",
  icon: Home
}, {
  title: "Outdated finishes and worn materials",
  description: "Replacing failing laminate surfaces, tired honey oak woodwork, worn tile grouting, and inefficient lighting fixtures.",
  icon: Paintbrush
}, {
  title: "Preparing to sell for top value",
  description: "Investing in key focal updates and architectural cohesion that command peak market demand across metro Atlanta buyers.",
  icon: TrendingUp
}, {
  title: "Want a clean modern aesthetic",
  description: "Transitioning to architectural trim profiles, custom built-ins, cohesive stone countertops, and minimalist designer hardware.",
  icon: ShieldCheck
}];
export default function TimelineTriggers() {
  return <section id="timelineandtriggers" data-nav="light" className="bg-background text-foreground py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        
        {/* SECTION 1: TIMELINE FACTORS */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5 space-y-6">
            <div className="space-y-3">
              <Badge variant="outline" className="border-border text-foreground font-mono text-xs uppercase tracking-wide">
                Planning & Schedule
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-serif font-semibold tracking-tight text-foreground">
                Interior renovation timeline factors
              </h2>
            </div>
            
            <p className="text-muted-foreground text-base leading-relaxed">
              Every comprehensive residential transformation hinges on clear milestone pacing. We guide your Brookhaven project through four fundamental phases to ensure predictable delivery without unvetted delays.
            </p>

            <div className="overflow-hidden rounded-xl border border-border bg-card shadow-xs">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/kitchen_interior_planning_construction.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/kitchen_interior_planning_construction.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/kitchen_interior_planning_construction.png-wvc-sizes" alt="Architectural renovation floor plan review for an interior home remodel" loading="lazy" decoding="async" className="w-full h-64 object-cover object-center" />
              <div className="p-4 bg-muted/40 text-xs text-muted-foreground">
                Thorough pre-construction planning ensures City of Brookhaven permit compliance and material readiness.
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6">
            {timelineFactors.map((factor, index) => {
            const Icon = factor.icon;
            return <Card key={factor.step} data-index={index} className="border-border bg-card shadow-xs hover:border-primary/50 transition-colors flex flex-col justify-between">
                  <CardHeader className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-sm font-semibold text-primary">
                        {factor.step}
                      </span>
                      <div className="rounded-md bg-muted p-2 text-foreground">
                        <Icon className="size-5 text-foreground" />
                      </div>
                    </div>
                    <CardTitle className="text-lg font-serif font-semibold text-card-foreground">
                      {factor.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                      {factor.description}
                    </CardDescription>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>

        <div className="my-16 lg:my-24">
          <Separator className="bg-border" />
        </div>

        {/* SECTION 2: LIFE-MOMENT TRIGGERS */}
        <div>
          <div className="max-w-3xl space-y-4 mb-12">
            <Badge variant="outline" className="border-border text-foreground font-mono text-xs uppercase tracking-wide">
              Evaluation Guide
            </Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold tracking-tight text-foreground">
              Is it time to update your interior?
            </h2>
            <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
              Recognizing when your living space no longer serves your lifestyle is the first step toward enduring transformation. Common life moments that prompt Brookhaven homeowners to remodel:
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7 space-y-3">
              {interiorTriggers.map((trigger, i) => {
              const Icon = trigger.icon;
              return <div key={trigger.title} data-index={i} className="group rounded-xl border border-border bg-card p-5 shadow-xs transition-all hover:border-primary hover:shadow-sm flex items-start gap-4">
                    <div className="rounded-lg bg-muted p-2.5 shrink-0 text-foreground group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                      <Icon className="size-5" />
                    </div>
                    <div className="space-y-1">
                      <h3 className="text-base font-semibold text-foreground font-sans">
                        {trigger.title}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {trigger.description}
                      </p>
                    </div>
                  </div>;
            })}
            </div>

            <div className="lg:col-span-5 space-y-6">
              <div className="relative overflow-hidden rounded-2xl border border-border bg-card shadow-md">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/modern_brookhaven_renovated_interior_living.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/modern_brookhaven_renovated_interior_living.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/modern_brookhaven_renovated_interior_living.png-wvc-sizes" alt="Bright and open renovated interior living room with updated architectural details" loading="lazy" decoding="async" className="w-full h-80 sm:h-96 object-cover object-center" />
                <div className="p-6 bg-card space-y-4">
                  <h4 className="text-xl font-serif font-semibold text-foreground">
                    Discuss your project scope
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Speak directly with Brookhaven Elevation Renovations about architectural feasibility, timeline windows, and local permit management.
                  </p>
                  <Button asChild className="w-full bg-primary text-primary-foreground hover:bg-primary/90">
                    <Link to="/ContactRequestAnEstimatePage">
                      Request an estimate
                      <ArrowRight className="size-4 ml-1" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>;
}